import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { metaApi, dashboardApi, userApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import PageHeader from '../../components/PageHeader.jsx';
import Card from '../../components/Card.jsx';
import StatCard from '../../components/StatCard.jsx';
import Badge from '../../components/Badge.jsx';
import Spinner from '../../components/Spinner.jsx';

/**
 * IT view: is the platform up, and what is this role allowed to see?
 *
 * The counters are platform-shaped, not sales-shaped: how many accounts and how
 * much delivery work the API is actually serving this team, plus the size of
 * the user base. The head count comes from the directory rather than the
 * dashboard - the IT dashboard is a delivery payload and deliberately carries
 * no company-wide figures.
 */
const SystemHealthPage = () => {
  const { t } = useTranslation();
  const [health, setHealth] = useState(null);
  const { data: meta } = useApi(() => metaApi.get(), []);
  const { data: dashboard, loading } = useApi(() => dashboardApi.get(), []);
  const { data: directory } = useApi(() => userApi.directory(), []);

  useEffect(() => {
    metaApi.health().then(setHealth).catch(() => setHealth({ status: 'down' }));
  }, []);

  if (loading) return <Spinner full />;

  return (
    <>
      <PageHeader title={t('it.systemHealth')} />

      <div className="stat-grid">
        <StatCard
          label={t('it.apiStatus')}
          value={health?.status === 'ok' ? 'OK' : '...'}
          hint={health?.env}
          tone={health?.status === 'ok' ? 'success' : 'danger'}
        />
        <StatCard label={t('dashboard.customers')} value={dashboard?.kpis?.customers ?? 0} />
        <StatCard label={t('dashboard.projects')} value={dashboard?.kpis?.projects ?? 0} />
        <StatCard label={t('dashboard.activeUsers')} value={(directory?.items ?? []).length} />
      </div>

      <Card title={t('it.accessRule')}>
        <p className="rule-banner">
          <Badge
            value={meta?.rules?.itFullCustomerAccess ? 'ACTIVE' : 'DISABLED'}
            label={meta?.rules?.itFullCustomerAccess ? t('it.accessRuleAll') : t('it.accessRuleScoped')}
          />
        </p>
        <p className="muted small">{t('it.accessRuleHint')}</p>
      </Card>

      <Card title={t('settings.roles')}>
        <div className="perm-grid">
          {Object.entries(meta?.rolePermissions ?? {}).map(([role, permissions]) => (
            <div key={role} className="perm-grid__cell">
              <Badge value={role} label={t(`roles.${role}`)} />
              <ul className="perm-list">
                {permissions.map((permission) => (
                  <li key={permission}>
                    <code>{permission}</code>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
};

export default SystemHealthPage;
